<template>
<!-- WHAT: A components to collect metrics and whereClause and to be used as the base for other lake service models.
   HISTORY:
   * V260310.1: Modified init() to fix a bug where metrics were not displayed if the schema had no numeric columns.
   * V251027.1: Supported 2-way binding: Initialized form fields from v-model.
   * V250811.1: Introduced exclude prop for models with filter.
   * V250807.1: Introduced hasFilter prop to capture an optional filter field when it is requested.
   * V250708.1: Modified v-col sizes to always fill up the row.
   * V250707.1: Changed name to BtMetricsModel + Added noLabel prop to hide label when it is not required (In this case, the selected metric will be assigned automatically).
   * V250703.1: Added initialMetrics prop and modified logic to handle initializing metrics.
   * V250702.1: Watched max and modified some logic to handle its changes (for BtDatesModel).
   * V250630.1: Added 'sum' to METRIC_ITEMS.
   * V250627.2: Modified countOnColumns to add DISTINCT in front of _id and dedupHash + Added duplicate validations + Made SUBMIT btn enabled/disabled correctly.
   * V250625.1: 1st release.
-->
<v-form lazy-validation
   ref="form"
   v-model="isFormValid"
>
   <v-row v-if="errMsg">
      <v-col cols="12" class="pt-2 pb-0 red--text">
         <v-alert v-if="errMsg" dense outlined
            class="mb-0"
            type="error"
         >
            <ul class="pl-2">
               <li v-for="item in errMsg.split('!').map(i => i.trim()).filter(Boolean)" :key="item">{{item}}!</li>
            </ul>
         </v-alert>
      </v-col>
   </v-row>
   <v-row v-for="(item, i) in metrics" :key="i">
      <v-col xs="12" sm="12" :md="colSize" class="py-0">
         <v-autocomplete dense persistent-hint required
            :ref="`metric${i}`"
            autocomplete="off"
            placeholder="select a metric..."
            :hint="item.metric ? `* Metric #${i + 1}` : ''"
            :disabled="item.isInitial"
            :items="metricItems"
            :rules="getMetricRules(i)"
            v-model="item.metric"
            @change="metricChanged(i)"
         >
            <template v-slot:prepend>
               <v-icon
                  class="py-2"
                  color="grey darken-1"
                  :disabled="!item.metric && i === metrics.length - 1"
                  @click="removeMetric(i)"
               >delete_forever</v-icon>
            </template>
         </v-autocomplete>
      </v-col>
      <v-col v-if="item.metric"
         xs="12" sm="12" :md="colSize" class="py-0"
      >
         <v-autocomplete dense persistent-hint required
            :ref="`onColumn${i}`"
            autocomplete="off"
            placeholder="select a column..."
            :hint="item.on ? `* On Column #${i + 1}` : ''"
            :disabled="item.isInitial"
            :items="item.metric === 'count' ? countOnColumns : numericOnColumns"
            :rules="[rules.required]"
            v-model="item.on"
            @change="onColumnChanged"
         ></v-autocomplete>
      </v-col>
      <v-col v-if="item.metric && !noLabel"
         xs="12" sm="12" :md="colSize" class="py-0"
      >
         <v-text-field dense persistent-hint required hide-spin-buttons
            :ref="`label${i}`"
            class="py-0"
            placeholder="specify a label..."
            :hint="item.label ? `* Label #${i + 1}` : ''"
            :disabled="item.isInitial"
            :rules="[rules.required, rules.validLabel]"
            v-model="item.label"
         ></v-text-field>
      </v-col>
      <v-col v-if="item.metric && hasFilter"
         xs="12" sm="12" :md="2*colSize+1" class="py-0 pr-0"
      >
         <v-text-field dense persistent-hint required hide-spin-buttons
            :ref="`filter${i}`"
            class="py-0"
            style="flex:0 0 98%; max-width:98%;"
            placeholder="specify a filter..."
            :hint="item.label ? `Filter #${i + 1}` : ''"
            :disabled="item.isInitial"
            :rules="[rules.whereClause]"
            v-model="item.filter"
         ></v-text-field>
      </v-col>
      <v-col v-if="item.metric && hasFilter"
         xs="12" sm="12" md="1" class="py-0 pl-0"
      >
         <!-- <v-tooltip left>
            <template v-slot:activator="{ on }">
               <v-switch dense hide-details
                  class="mb-0"
                  label="Exclude"
                  :disabled="isExcludeDisabled"
                  v-on="on"
                  v-model="item.exclude"
               ></v-switch>
            </template>
            <span>If an Exclude is on, date range will be added to the metric filters that are not excluded. Otherwise, the filter will be applied to the global Where Clause.</span>
         </v-tooltip> -->
         <v-tooltip left open-on-hover>
            <template v-slot:activator="{ on }">
               <div v-on="on">
                  <v-switch dense hide-details
                     class="mb-0"
                     label="Exclude"
                     :disabled="isExcludeDisabled"
                     v-model="item.exclude"
                  ></v-switch>
               </div>
            </template>
            <span>
               If "Exclude" is on, the date range will apply only to metrics not excluded.<br>
               Otherwise, the filter will be applied to the global "Where Clause".
            </span>
         </v-tooltip>
      </v-col>
   </v-row>
   <v-row>
      <v-col cols="12" class="pb-0">
            <!-- :disabled="metrics.length != max && metrics.length < 2" -->
         <v-textarea dense outlined persistent-hint required
            label="* Where Clause"
            rows="3"
            :rules="[rules.whereClause]"
            v-model="whereClause"
            @keyup="formChanged"
         ></v-textarea>
      </v-col>
   </v-row>
</v-form>
</template>

<script>
import { isEmpty } from '../mixins/bt-mixin.js';

const NAME = "BtMetricsModel";
const VERSION = 'V260310.1';
const METRIC_ITEMS = [
   { text: 'Count', value: 'count' },
   { text: 'Maximum', value: 'max' },
   { text: 'Minimum', value: 'min' },
   { text: 'Mean', value: 'mean' },
   { text: 'Median', value: 'median' },
   { text: 'Mode', value: 'mode' },
   { text: 'Standard Deviation', value: 'stddev' },
   { text: 'Sum', value: 'sum' }
];

class Metric {
   constructor(hasFilter, initVal) {
      this.metric = initVal?.metric || '';
      this.on = initVal?.on || '';
      this.label = initVal?.label || '';
      if (hasFilter) {
         this.filter = initVal?.filter || '';
         this.exclude = initVal?.exclude || false;
      }
   }
}

export default {
   name: NAME,

   props: {
      value: {
         type: Object,
         default: () => ({})
      },
      debug: {
         type: Boolean,
         default: false
      },
      hasFilter: {
         type: Boolean,
         default: false
      },
      initialMetrics: {
         type: Array,
         default: () => []
      },
      initialModel: {
         type: Boolean
         // default: true
      },
      max: {
         type: Number,
         default: Number.MAX_VALUE
      },
      noLabel: {
         type: Boolean,
         default: false
      },
      querySubmitted: {
         type: Boolean,
         required: true
      },
      schemas: {
         type: Array,
         required: true,
         default: () => []
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || 'Value is required!',
            validLabel: v =>
               /^[^\d\s-][^\s-]*$/.test(v) || 'Value must not start with a digit or contain spaces or dashes!',
            whereClause: value => {
               const val = value?.trim()?.toLowerCase();
               return !val
                  || (val?.startsWith('where ') && val?.length > 6)
                  || 'Value must start with "where " and include a condition after it!'
            }
         },
         countOnColumns: [],
         numericOnColumns: [],
         isFormValid: false,
         isFormChanged: false,
         metrics: [],
         whereClause: '',
         errMsg: ''
      }
   },

   computed: {
      colSize() {
         // return this.noLabel ? 6 : 4;
         if (this.noLabel) return 6;
         else if (this.hasFilter) return 2;
         else return 4;
      },

      metricItems() {
         return this.numericOnColumns.length ? METRIC_ITEMS : METRIC_ITEMS.slice(0, 1);
      },

      isExcludeDisabled() {
         return this.schemas.filter(item => item.column_name.toLowerCase() === 'event_date' && item.column_type === 'TIMESTAMP').length === 0;
      }
   },

   watch: {
      initialModel: {
         immediate: true,
         handler(val) {
            if (val) this.init();
         }
      },

      // // max: {
      // //    handler() {
      // //       // if (this.metrics.length >= val)
      // //       //    this.metrics.splice(0, this.metrics.length - 1);
      // //       // else if (this.metrics[this.metrics.length - 1].metric)
      // //       //    this.addMetric();
      // //       // // this.metrics = this.initialMetrics.map(item => ({
      // //       // //    ...item,
      // //       // //    isInitial: true
      // //       // // }));
      // //       // // this.addMetric();
      // //       // // this.formChanged();

      // //       if (this.schemas) this.init();
      // //    }
      // // },

      schemas: {
         immediate: true,
         deep: true,
         async handler(val) {
            // this.alert('in ' + NAME + '/watch: schemas=' + JSON.stringify(val));
            // // this.metrics = this.initialMetrics.map(item => ({
            // //    ...item,
            // //    isInitial: true
            // // }));
            // // this.addMetric();
            // // this.formChanged();

            // // this.init();
            // // this.whereClause = '';

            if (val?.length) {
               this.countOnColumns = [
                  '*',
                  ...val.map(col =>
                     ['_id', 'dedupHash'].includes(col.column_name)
                        ? `DISTINCT ${col.column_name}`
                        : col.column_name
                  )
               ];
               this.numericOnColumns = val.filter(col => col.column_type === 'BIGINT' || col.column_type === 'DOUBLE')
                  .map(col => col.column_name);
            } else {
               this.countOnColumns = [];
               this.numericOnColumns = [];
            }

            this.init();

            //commented for column-distinct
            // // // this.formChanged();
            this.isFormChanged = false;
         }
      },

      metrics: {
         // immediate: true,
         deep: true,
         async handler(val) {
            this.errMsg = '';
            const arrMetricOn = [...val.map(m => `'${m.metric}':'${m.on}':'${m.filter}'`)];
            const seenMetricOn = new Set();
            for (const item of arrMetricOn) {
               if (seenMetricOn.has(item)) {
                  // this.errMsg += `${item.replace(':', ' of ')} is duplicated!`;
                  const itemParts = item.split(':');
                  this.errMsg += `${itemParts[0]} of ${itemParts[1]} is duplicated!`;
               } else {
                  seenMetricOn.add(item);
               }
            }

            const seenLabels = new Set();
            for (const item of val) {
               if (item.label && seenLabels.has(item.label.toLowerCase()))
                  this.errMsg += `'${item.label}' is duplicated!`;
               seenLabels.add(item.label.toLowerCase());
            }

            this.$nextTick(() => {
               this.formChanged();
            });
         }
      },

      querySubmitted: {
         handler(val) {
            if (val) this.isFormChanged = false;
         }
      }
   },

   methods: {
      alert(msg) {
         if (this.debug) {
            alert(`${NAME} ${VERSION} says:\n${msg}`);
         }
      },

      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} ${VERSION} says => ${msg}`);
         }
      },

      init() {
         //V260310: if (this.numericOnColumns.length) {
            const { metrics, whereClause } = this.value;

            this.metrics = [
               ...this.initialMetrics.map(item => ({
                  ...item,
                  isInitial: true
               })),
               ...(isEmpty(metrics) ? [] : metrics)
            ];
            this.whereClause = whereClause || '';
            
            this.addMetric();
         // V260310
         // } else {
         //    this.metrics = [];
         //    this.whereClause = '';
         // }
      },

      getMetricRules(index) {
         if (index === 0 || (index < this.metrics.length - 1))
            return [this.rules.required];
         else
            return [];
      },

      addMetric() {
         const len = this.metrics.length;
         if ((this.max && len === 0) || (len < this.max && this.metrics[len - 1]?.metric)) {
            this.metrics.push(new Metric(this.hasFilter));
         }
      },

      metricChanged(index) {
         // alert('in metricChanged(): index=' + index);
         const currMetric = this.metrics[index];
         currMetric.on = '';  //TODO: empty it if the value doesn't exist in the dropdown
         if (this.noLabel)
            currMetric.label = this.metricItems.find(item => item.value === currMetric.metric)?.text;
         this.addMetric();
         setTimeout(() => {
            this.$refs.form?.validate();
            // this.$refs[`onColumn${index}`]?.focus();
         }, 100);
      },

      onColumnChanged(index) {
         setTimeout(() => {
            this.$refs[`label${index}`]?.focus();
         }, 100);
      },

      removeMetric(ind) {
         if (confirm('Are you sure?')) {
            this.metrics.splice(ind, 1);
         }
      },

      formChanged() {
         let emitVal = false;
         this.isFormValid = this.$refs.form?.validate();
         if (this.isFormValid && !this.errMsg) {
            const len = this.metrics.length;
            if (len === this.max || len > 1) {
               const metricsData = {};
               const metrics = (len === this.max ? [...this.metrics] : this.metrics.slice(0, -1))
                  .map(({ isInitial, ...rest }) => {
                     if (rest.filter === "") {
                        delete rest.filter;
                     }
                     return rest;
                  });

               if (metrics?.length)
                  metricsData.metrics = [...metrics];
               if (this.whereClause.trim())
                  metricsData.whereClause = this.whereClause.trim();

               this.$emit('input', metricsData);
               emitVal = true;
            }
         }

         this.$emit('form-valid', emitVal);
      }
   },

   mounted() {
      // alert(`in ${NAME}.create(): max=${this.max}`);
      if (this.initialModel === undefined)
         this.init();
   }
}
</script>
